import React from 'react';
import { Link } from 'react-router-dom';
import { Shield, Network, Server, Headphones, ArrowRight, CheckCircle, Monitor, Globe, Lock, Zap } from 'lucide-react';
import './Home.css';

const Home = () => {
  const heroFeatures = [
    { icon: <Shield size={24} />, title: 'SOC Services', description: '24x7 Security Operations' },
    { icon: <Network size={24} />, title: 'NOC Services', description: 'Network Operations Excellence' },
    { icon: <Server size={24} />, title: 'Data Center', description: 'Infrastructure Management' },
    { icon: <Headphones size={24} />, title: 'Product Support', description: 'Global Technical Support' }
  ];

  const unsplashHeroImages = [
    'https://images.unsplash.com/photo-1550745665-5bcfa8209023?w=800&h=600&fit=crop',
    'https://images.unsplash.com/photo-1558494949-ef010cbffb31?w=800&h=600&fit=crop',
    'https://images.unsplash.com/photo-1580678683292-cd3ec928cde0?w=800&h=600&fit=crop',
    'https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=800&h=600&fit=crop'
  ];

  const capabilities = [
    { 
      icon: <Monitor size={48} />, 
      title: 'Advanced Monitoring', 
      description: 'Real-time monitoring and alerting systems for proactive issue detection and resolution.',
      features: ['24x7 Surveillance', 'Automated Alerts', 'Performance Metrics']
    },
    { 
      icon: <Globe size={48} />, 
      title: 'Global Operations', 
      description: 'Worldwide infrastructure management with follow-the-sun support model.',
      features: ['Multi-Region Support', 'Global Teams', 'Time Zone Coverage']
    },
    { 
      icon: <Lock size={48} />, 
      title: 'Enterprise Security', 
      description: 'Comprehensive security solutions protecting your critical assets and data.',
      features: ['Threat Detection', 'Security Analytics', 'Compliance Management']
    },
    { 
      icon: <Zap size={48} />, 
      title: 'High Performance', 
      description: 'Optimized systems delivering maximum uptime and operational efficiency.',
      features: ['99.9% Uptime', 'Fast Response', 'Scalable Solutions']
    }
  ];

  const values = [
    'Integrity & Transparency',
    'Customer First',
    'Technical Excellence',
    'Continuous Innovation',
    'Accountability'
  ];

  return (
    <div className="home">
      {/* Microsoft-style Hero Section */}
      <section className="microsoft-hero">
        <div className="hero-container">
          <div className="hero-content">
            <div className="hero-badge">Enterprise IT Solutions</div>
            <h1 className="hero-title">
              Kevitco Technologies
            </h1>
            <h2 className="hero-subtitle">
              Powering IT Operations. Securing the Future.
            </h2>
            <p className="hero-description">
              A next-generation IT services and solutions company delivering high-performance, 
              secure, and scalable technology services to global enterprises. We specialize in 
              SOC, NOC, Data Center Operations, and Product Support Services.
            </p>
            <div className="hero-actions">
              <Link to="/services" className="btn-primary">
                Explore Services <ArrowRight size={20} />
              </Link>
              <Link to="/contact" className="btn-secondary">
                Contact Us
              </Link>
            </div>
          </div>
          <div className="hero-features">
            {heroFeatures.map((feature, index) => (
              <div key={index} className="hero-feature-card">
                <div className="feature-icon">{feature.icon}</div>
                <div className="feature-content">
                  <h4>{feature.title}</h4>
                  <p>{feature.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
        <div className="hero-background">
          {/* <img 
            src={unsplashHeroImages[0]} 
            alt="Hero Background" 
            className="hero-bg-image"
          /> */}
          <div className="hero-overlay"></div>
        </div>
      </section>

      {/* Capabilities Section */}
      <section className="capabilities-section">
        <div className="container">
          <div className="section-header">
            <h2 className="section-title">Enterprise Capabilities</h2>
            <p className="section-subtitle">
              Comprehensive solutions designed for modern enterprise operations
            </p>
          </div>
          <div className="capabilities-grid">
            {capabilities.map((capability, index) => (
              <div key={index} className="capability-card">
                <div className="capability-icon">{capability.icon}</div>
                <h3>{capability.title}</h3>
                <p>{capability.description}</p>
                <ul className="capability-features">
                  {capability.features.map((feature, idx) => (
                    <li key={idx}>
                      <CheckCircle size={16} />
                      {feature}
                    </li>
                  ))}
                </ul>
                <Link to="/services" className="capability-link">
                  Learn more <ArrowRight size={16} />
                </Link>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Section */}
      <section className="why-section">
        <div className="container">
          <div className="why-content">
            <div className="why-text">
              <div className="section-header">
                <h2 className="section-title">Why Choose Kevitco?</h2>
                <p className="section-subtitle">
                  Built on experience, driven by excellence
                </p>
              </div>
              <div className="why-points">
                <div className="why-point">
                  <h4>Global Experience</h4>
                  <p>Team with extensive experience managing enterprise IT operations worldwide</p>
                </div>
                <div className="why-point">
                  <h4>24x7 Operations</h4>
                  <p>Round-the-clock support with follow-the-sun delivery model</p>
                </div>
                <div className="why-point">
                  <h4>Industry Best Practices</h4>
                  <p>Proven methodologies and frameworks for operational excellence</p>
                </div>
              </div>
            </div>
            <div className="why-values">
              <h3>Our Core Values</h3>
              <div className="values-list">
                {values.map((value, index) => (
                  <div key={index} className="value-item">
                    <CheckCircle size={20} />
                    <span>{value}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="cta-section">
        <div className="container">
          <div className="cta-content">
            <h2>Ready to Transform Your IT Operations?</h2>
            <p>Get in touch with our team to discuss your requirements and discover how we can help your business thrive.</p>
            <div className="cta-actions">
              <Link to="/contact" className="btn-primary">
                Get Started Today
              </Link>
              <Link to="/services" className="btn-secondary">
                View Services
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
